// Mock API responses for development testing
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Mock data
const mockBots = [
  {
    id: "1",
    name: "Customer Support Bot",
    description: "Handles customer inquiries and support tickets",
    status: "active",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-20T14:30:00Z",
    sourceCount: 12,
    queryCount: 1543,
  },
  {
    id: "2",
    name: "Product Documentation Bot",
    description: "Provides answers from product documentation and guides",
    status: "active",
    createdAt: "2024-01-10T09:15:00Z",
    updatedAt: "2024-01-22T11:45:00Z",
    sourceCount: 8,
    queryCount: 892,
  },
  {
    id: "3",
    name: "HR Policy Bot",
    description: "Answers questions about company policies and procedures",
    status: "inactive",
    createdAt: "2024-01-05T16:20:00Z",
    updatedAt: "2024-01-18T13:10:00Z",
    sourceCount: 5,
    queryCount: 234,
  },
];

const mockSources = [
  {
    id: "1",
    botId: "1",
    name: "FAQ Document",
    type: "pdf",
    status: "processed",
    url: "https://example.com/faq.pdf",
    createdAt: "2024-01-15T10:30:00Z",
    processedAt: "2024-01-15T10:35:00Z",
    chunkCount: 45,
  },
  {
    id: "2",
    botId: "1",
    name: "Support Guidelines",
    type: "text",
    status: "processing",
    content: "Customer support guidelines and best practices...",
    createdAt: "2024-01-20T14:00:00Z",
    chunkCount: 0,
  },
];

// Mock API implementation
export const mockApi = {
  // Generic HTTP methods with mock responses
  get: async (url) => {
    await delay(300); // Simulate network delay

    if (url === "/auth/check") {
      return {
        data: {
          user: { id: "1", email: "test@example.com", name: "Test User" },
        },
      };
    }

    if (url === "/auth/me" || url === "/me") {
      return {
        data: {
          user: {
            id: "dev-user",
            email: "dev@example.com",
            name: "Development User",
            origin: "rag",
          },
          tenants: [
            {
              id: "dev-tenant",
              name: "Development Organization",
              origin: "rag",
            },
          ],
        },
      };
    }

    if (url === "/health") {
      return { data: { status: "OK", timestamp: new Date().toISOString() } };
    }

    if (url === "/bots") {
      return { data: mockBots };
    }

    if (url.match(/^\/bots\/(\d+)$/)) {
      const id = url.match(/^\/bots\/(\d+)$/)[1];
      const bot = mockBots.find((b) => b.id === id);
      if (!bot) throw new Error("Bot not found");
      return { data: bot };
    }

    if (url.match(/^\/bots\/(\d+)\/sources$/)) {
      const botId = url.match(/^\/bots\/(\d+)\/sources$/)[1];
      const sources = mockSources.filter((s) => s.botId === botId);
      return { data: sources };
    }

    throw new Error(`Mock API: Unhandled GET ${url}`);
  },

  post: async (url, data) => {
    await delay(500);

    if (url === "/bots") {
      const newBot = {
        id: String(mockBots.length + 1),
        ...data,
        status: "active",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sourceCount: 0,
        queryCount: 0,
      };
      mockBots.push(newBot);
      return { data: newBot };
    }

    if (url.match(/^\/bots\/(\d+)\/sources$/)) {
      const botId = url.match(/^\/bots\/(\d+)\/sources$/)[1];
      const newSource = {
        id: String(mockSources.length + 1),
        botId,
        ...data,
        status: "processing",
        createdAt: new Date().toISOString(),
        chunkCount: 0,
      };
      mockSources.push(newSource);
      return { data: newSource };
    }

    if (url.match(/^\/bots\/(\d+)\/query$/)) {
      const botId = url.match(/^\/bots\/(\d+)\/query$/)[1];
      return {
        data: {
          answer: `This is a mock response to your question: "${data.question}". In a real implementation, this would be generated by the RAG system using the bot's knowledge base.`,
          sources: [
            { id: "1", name: "FAQ Document", relevance: 0.95 },
            { id: "2", name: "Support Guidelines", relevance: 0.87 },
          ],
          confidence: 0.91,
        },
      };
    }

    if (url === "/auth/signup") {
      // Simulate validation
      if (!data.email || !data.name || !data.password) {
        throw new Error("Missing required fields");
      }
      if (data.email === "existing@example.com") {
        throw new Error("Email already exists");
      }
      return {
        data: {
          success: true,
          message: "User registered successfully",
        },
      };
    }

    if (url === "/auth/login") {
      // Simulate validation
      if (!data.email || !data.password) {
        throw new Error("Missing email or password");
      }
      if (data.email === "invalid@example.com" || data.password === "wrong") {
        throw new Error("Invalid credentials");
      }
      return {
        data: {
          success: true,
          message: "Login successful",
        },
      };
    }

    if (url === "/uploads/sas") {
      return {
        data: {
          uploadUrl: "https://mock-storage.example.com/upload",
          fields: {
            key: `uploads/${data.filename}`,
            "Content-Type": data.contentType,
          },
        },
      };
    }

    throw new Error(`Mock API: Unhandled POST ${url}`);
  },

  put: async (url, data) => {
    await delay(400);

    if (url.match(/^\/bots\/(\d+)$/)) {
      const id = url.match(/^\/bots\/(\d+)$/)[1];
      const botIndex = mockBots.findIndex((b) => b.id === id);
      if (botIndex === -1) throw new Error("Bot not found");

      mockBots[botIndex] = {
        ...mockBots[botIndex],
        ...data,
        updatedAt: new Date().toISOString(),
      };
      return { data: mockBots[botIndex] };
    }

    throw new Error(`Mock API: Unhandled PUT ${url}`);
  },

  delete: async (url) => {
    await delay(300);

    if (url.match(/^\/bots\/(\d+)$/)) {
      const id = url.match(/^\/bots\/(\d+)$/)[1];
      const botIndex = mockBots.findIndex((b) => b.id === id);
      if (botIndex === -1) throw new Error("Bot not found");

      mockBots.splice(botIndex, 1);
      return { data: { success: true } };
    }

    if (url.match(/^\/bots\/(\d+)\/sources\/(\d+)$/)) {
      const [, botId, sourceId] = url.match(/^\/bots\/(\d+)\/sources\/(\d+)$/);
      const sourceIndex = mockSources.findIndex(
        (s) => s.id === sourceId && s.botId === botId
      );
      if (sourceIndex === -1) throw new Error("Source not found");

      mockSources.splice(sourceIndex, 1);
      return { data: { success: true } };
    }

    throw new Error(`Mock API: Unhandled DELETE ${url}`);
  },

  // Convenience methods
  health: () => mockApi.get("/health"),

  auth: {
    signup: (userData) => mockApi.post("/auth/signup", userData),
    login: (credentials) => mockApi.post("/auth/login", credentials),
    check: () => mockApi.get("/auth/check"),
    me: () =>
      Promise.resolve({
        data: {
          user: {
            id: "dev-user",
            email: "dev@example.com",
            name: "Development User",
            origin: "rag",
          },
          tenants: [
            {
              id: "dev-tenant",
              name: "Development Organization",
              origin: "rag",
            },
          ],
        },
      }),
    logout: () => Promise.resolve({ data: { success: true } }),
  },

  bots: {
    list: () => mockApi.get("/bots"),
    create: (data) => mockApi.post("/bots", data),
    get: (id) => mockApi.get(`/bots/${id}`),
    update: (id, data) => mockApi.put(`/bots/${id}`, data),
    delete: (id) => mockApi.delete(`/bots/${id}`),
    updateWidgetConfig: (id, widgetConfig) => {
      return Promise.resolve({
        message: "Widget configuration updated successfully",
        bot: {
          id: id,
          name: `Mock Bot ${id}`,
          widget_code: `mock-widget-${id}`,
          embed_code: `<script src="http://localhost:3000/widget.js?code=mock-widget-${id}"></script>`,
        },
      });
    },
    getWidgetEmbed: (id) => {
      return Promise.resolve({
        widget_code: `mock-widget-${id}`,
        embed_code: `<script src="http://localhost:3000/widget.js?code=mock-widget-${id}"></script>`,
        embed_url: `http://localhost:3000/widget.js?code=mock-widget-${id}`,
        preview_url: `http://localhost:3000/widget-test?code=mock-widget-${id}`,
        has_config: true,
      });
    },
  },

  sources: {
    list: (botId) => mockApi.get(`/sources/${botId}`),
    create: (botId, data) => mockApi.post(`/sources/${botId}`, data),
    get: (botId, sourceId) => mockApi.get(`/sources/${botId}/${sourceId}`),
    delete: (botId, sourceId) =>
      mockApi.delete(`/sources/${botId}/${sourceId}`),
    reprocess: (botId, sourceId) =>
      mockApi.post(`/sources/${botId}/${sourceId}/reprocess`),
  },

  uploads: {
    getSasUrl: (filename, contentType) =>
      mockApi.post("/uploads/sas", { filename, contentType }),
  },

  query: {
    ask: (botId, question) =>
      mockApi.post(`/bots/${botId}/query`, { question }),
  },
};
